import { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';

/**
 * Hook to detect if an element's text is truncated (single-line or multi-line).
 * 
 * @param deps - Optional dependency array to force re-check when text changes
 * @returns [ref, isTruncated] - Attach ref to the element with truncate/line-clamp styles
 */
export function useIsTruncated<T extends HTMLElement = HTMLElement>(
  deps: React.DependencyList = []
) {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const element = ref.current;
    if (!element) return;

    // Add 1px tolerance to handle subpixel rounding issues
    const tolerance = 1;

    // Single-line truncation (text-overflow: ellipsis / truncate class)
    const isOverflowX = element.scrollWidth > element.clientWidth + tolerance;

    // Multi-line truncation (line-clamp-* classes)
    const isOverflowY = element.scrollHeight > element.clientHeight + tolerance;

    setIsTruncated(isOverflowX || isOverflowY);
  }, []);

  // Use useLayoutEffect for synchronous measurement after DOM updates
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Measure after layout but before paint
    checkTruncation();

    // Also check after a frame in case of async rendering
    const frameId = requestAnimationFrame(() => {
      checkTruncation();
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkTruncation, ...deps]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Use ResizeObserver to detect container size changes
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        checkTruncation();
      });
      resizeObserver.observe(element);
    }

    // Re-check after fonts load (widths can change after font rendering)
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        checkTruncation();
      });
    }

    // Fallback to window resize
    window.addEventListener('resize', checkTruncation);

    return () => {
      window.removeEventListener('resize', checkTruncation);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [checkTruncation]);

  return [ref, isTruncated] as const;
}
